import { ConektoUserPagingDto } from '@/__generated';
import BooleanTag from '@/components/app/BooleanTag';
import ZTable, { IColumnType, IFilterItem, ITableRef } from '@/components/ZTable';
import { dateTimeDisplayFormat } from '@/config';
import { PassengerUserSource } from '@/constants';
import MainContent from '@/layouts/MainContent';
import { Button, message, Modal, Space, Tag, Tooltip } from 'antd';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { EyeOutlined, InfoOutlined, WalletTwoTone } from '@ant-design/icons';
import { apiSdk } from '@/httpClient';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { ExportPdfBtn } from '@/components/app/ExportBtn';
import { exportService } from '@/services/exportService';
import PassengerInfoModal from './PassengerInfoModal';
import UserFollowupModal from './UserFollowupModal';

const columns: IColumnType<ConektoUserPagingDto>[] = [
  {
    title: 'base.FullName',
    dataIndex: 'fullName',
    width: 200,
  },
  {
    title: 'passengerAppUser_RegisterSource',
    dataIndex: 'userSource',
    render: v => <Tag>{PassengerUserSource.__getValue(v)}</Tag>,
    width: 130,
  },
  {
    title: 'passengerAppUser_PhoneNumber',
    dataIndex: 'firstName',
    render: (_, record) => [record.phoneCode, record.phoneNumber].filter(x => x).join('-'),
    width: 120,
    ellipsis: true,
  },
  {
    title: 'passengerAppUser_PhoneNumberConfirmed',
    dataIndex: 'phoneNumberConfirmed',
    render: v => <BooleanTag value={v} />,
    width: 150,
    align: 'center',
    ellipsis: true,
  },
  {
    title: 'base.Email',
    dataIndex: 'emailAddress',
    width: 150,
    ellipsis: true,
  },
  {
    title: 'adminUsersPage_emailConfirmed',
    dataIndex: 'emailConfirmed',
    render: v => <BooleanTag value={v} />,
    align: 'center',
    width: 130,
    ellipsis: true,
  },

  {
    title: 'passengerAppUser_IsActive',
    dataIndex: 'isActive',
    align: 'center',
    render: v => <BooleanTag value={v} />,
    width: 120,
    ellipsis: true,
  },
  {
    title: 'passengerAppUser_RegisterDate',
    dataIndex: 'createdOn',
    align: 'center',
    render: v => dateTimeDisplayFormat.dateTime(v),
    width: 170,
    ellipsis: true,
  },
  {
    title: 'passengerAppUser_WalletBlocked',
    dataIndex: 'isUserWalletBlocked',
    align: 'center',
    render: v => <BooleanTag value={v} />,
    width: 120,
  },
];
const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'fullName', label: 'base.FullName' },
  { type: 'TEXT', name: 'phoneNumber', label: 'passengerAppUser_PhoneNumber' },
  { type: 'TEXT', name: 'EmailAddress', label: 'base.Email' },
];
const PAGE_TITLE = 'passengerAppUser_Title';

const PassengerAppUserPage = () => {
  const zTb = React.useRef<ITableRef>();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [passengerInfoId, setPassengerInfoId] = React.useState<string>();
  const [userFollowupId, setUserFollowupId] = React.useState<string>();
  const changePassengerWalletStatus = (userId: string, nextStatus: boolean) => {
    Modal.confirm({
      title: t('passengerAppUser_BlockPassengerWalletConfirm'),
      onOk: () => {
        setIsLoading(true);
        apiSdk.LianeUserManagementService.blockWallet({
          id: userId,
          isBlocked: nextStatus,
        })
          .then(res => {
            if (res.success) {
              message.success(t('Update Success!'));
              zTb.current?.reload();
            } else {
              defaultApiErrorHandle(res);
            }
          })
          .catch(defaultApiErrorHandle)
          .finally(() => {
            setIsLoading(false);
          });
      },
    });
  };

  const onExportPdf = () => {
    exportService.exportPassengerAppUsers(zTb.current?.getFilter()).toPdf();
  };
  return (
    <MainContent
      title={t(PAGE_TITLE)}
      isLoading={isLoading}
      extraBtns={
        <Space>
          <ExportPdfBtn onClick={onExportPdf} />
        </Space>
      }
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<ConektoUserPagingDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/liane-users/get-liane-users"
          bordered
          filters={filters}
          order_by="createdOn"
          order_by_asc={false}
          allowEdit={false}
          allowDelete={false}
          actionColumnWidth={100}
          extraActions={[
            {
              render: v => (
                <Space>
                  <Tooltip title={t('passengerAppUser_ShowPassengerInfo')}>
                    <Button onClick={() => setPassengerInfoId(v.id)} size="small" type="default" icon={<InfoOutlined />} />
                  </Tooltip>
                  <Tooltip title={t('passengerAppUser_UserFollowupsDetails')}>
                    <Button onClick={() => setUserFollowupId(v.id)} size="small" type="primary" icon={<EyeOutlined />} />
                  </Tooltip>
                  <Tooltip title={v.isUserWalletBlocked ? t('passengerAppUser_UnBlockWallet') : t('passengerAppUser_BlockWallet')}>
                    <Button
                      onClick={() => {
                        changePassengerWalletStatus(v.id || '', !v.isUserWalletBlocked);
                      }}
                      size="small"
                      type="primary"
                      danger={!v.isUserWalletBlocked}
                      icon={<WalletTwoTone />}
                    />
                  </Tooltip>
                </Space>
              ),
            },
          ]}
          searchBtnAlignLeft
        ></ZTable>
      </div>
      {passengerInfoId ? <PassengerInfoModal passengerId={passengerInfoId} onClose={() => setPassengerInfoId(undefined)} /> : null}

      {userFollowupId ? <UserFollowupModal lianUserId={userFollowupId} onClose={() => setUserFollowupId(undefined)} /> : null}
    </MainContent>
  );
};

export default PassengerAppUserPage;
